import { Loader2, Monitor, ShieldOff } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

/**
 * Surface-agnostic active-sessions panel. Used by both `/profile` (admin) and
 * `/portal/profile` (client portal). Same dumb-component contract as
 * `ProfileForm` — the caller wires the orval-generated hooks and passes them
 * down so this file stays free of API-client knowledge.
 *
 * UI rules:
 * - The row flagged `isCurrent` shows a "(this device)" pill and its revoke
 *   button is disabled — you can't sign yourself out of the device you're
 *   reading the page on.
 * - Revoked rows render greyed-out so the user has a visible "this is the
 *   one I revoked yesterday" trail.
 * - The "Sign out other devices" button calls the bulk-revoke endpoint and
 *   toasts the revoked count.
 */
export interface SessionRow {
  id: string;
  ipAddress: string | null | undefined;
  userAgent: string | null | undefined;
  createdAt: string;
  expiresAt: string;
  revokedAt: string | null | undefined;
  isCurrent: boolean;
}

export interface SessionsCardProps {
  sessions: SessionRow[];
  isLoading?: boolean;
  /** Revoke a single session. Caller mutates + refetches. */
  onRevokeSession: (sessionId: string) => Promise<void> | void;
  /** Revoke every session except the current one. Returns the count revoked. */
  onRevokeOthers: () => Promise<{ revokedCount: number }>;
}

export function SessionsCard({
  sessions,
  isLoading,
  onRevokeSession,
  onRevokeOthers,
}: SessionsCardProps) {
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [bulkRevoking, setBulkRevoking] = useState(false);

  const activeOtherSessions = sessions.filter((s) => !s.revokedAt && !s.isCurrent);
  const canBulkRevoke = activeOtherSessions.length > 0;

  async function handleRevokeOne(id: string) {
    setPendingId(id);
    try {
      await onRevokeSession(id);
      toast.success('Session revoked.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not revoke session.');
    } finally {
      setPendingId(null);
    }
  }

  async function handleRevokeOthers() {
    setBulkRevoking(true);
    try {
      const { revokedCount } = await onRevokeOthers();
      toast.success(
        revokedCount === 0
          ? 'No other active sessions to revoke.'
          : `Signed out of ${revokedCount} other device${revokedCount === 1 ? '' : 's'}.`,
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not revoke other sessions.');
    } finally {
      setBulkRevoking(false);
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle>Active sessions</CardTitle>
          <CardDescription>
            Devices currently signed in to your account. Revoke any session you don't recognise.
          </CardDescription>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleRevokeOthers}
          disabled={!canBulkRevoke || bulkRevoking || isLoading}
        >
          {bulkRevoking ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldOff className="mr-2 h-4 w-4" />}
          Sign out other devices
        </Button>
      </CardHeader>

      <CardContent className="space-y-3">
        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading sessions…
          </div>
        )}

        {!isLoading && sessions.length === 0 && (
          <p className="text-sm text-muted-foreground">No sessions on record.</p>
        )}

        {/*
          Cap the list at ~3 rows tall and scroll inside the card so a user
          with many active sessions doesn't push every other section of the
          profile page off-screen. `max-h-64` ≈ 16rem ≈ 3 row heights at the
          current `p-3` padding; the inner `space-y-2` keeps row spacing
          consistent now that the parent's `space-y-3` no longer wraps each
          item. `pr-1` reserves a sliver for the scrollbar so rows don't
          shift when it appears.
        */}
        <div className="max-h-64 space-y-2 overflow-y-auto pr-1">
        {!isLoading &&
          sessions.map((session) => {
            const revoked = !!session.revokedAt;
            return (
              <div
                key={session.id}
                className={[
                  'flex items-start justify-between gap-4 rounded-md border border-border/60 p-3',
                  revoked ? 'opacity-60' : '',
                ].join(' ')}
              >
                <div className="flex items-start gap-3">
                  <Monitor className="mt-0.5 h-4 w-4 text-muted-foreground" />
                  <div className="space-y-1 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{session.ipAddress ?? 'Unknown IP'}</span>
                      {session.isCurrent && (
                        <Badge variant="secondary" className="text-[10px]">this device</Badge>
                      )}
                      {revoked && (
                        <Badge variant="outline" className="text-[10px]">revoked</Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {session.userAgent ?? 'Unknown device'}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      Started {new Date(session.createdAt).toLocaleString()}
                      {' · '}
                      Expires {new Date(session.expiresAt).toLocaleString()}
                      {session.revokedAt && (
                        <>{' · '}Revoked {new Date(session.revokedAt).toLocaleString()}</>
                      )}
                    </div>
                  </div>
                </div>

                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={session.isCurrent || revoked || pendingId === session.id}
                  onClick={() => handleRevokeOne(session.id)}
                >
                  {pendingId === session.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Revoke'
                  )}
                </Button>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
